import * as React from 'react';
import Viewport from '../../widgets/Viewport';
import { OnboardingSections } from '../../constants';
import './OnboardingView.scss';
import OnboardingNavbar from './OnboardingNavbar';
import { PageTitle } from '../../widgets/PageTitle';
import OnboardingContacts from './contacts/OnboardingContacts';
import OnboardingInternetExposure from './internet-exposure/OnboardingInternetExposure';
import OnboardingSiteConfig from './site-config/OnboardingSiteConfig';
import OnboardingCloudSensorConfig from './cloud-sensor-config/OnboardingCloudSensorConfig';
import OnboardingChecklist from './checklist/OnboardingChecklist';
import { useSelector } from 'react-redux';
import { onboardingSelector } from '../../reducers/onboarding';
import { RefMap } from '../../types';
import OnboardingEndpointConfig from './endpoint-config/OnboardingEndpointConfig';
import { sessionSelector } from '../../reducers/session';

const OnboardingView = () => {
  const checklistRef = React.useRef<HTMLDivElement>(null);
  const contactsRef = React.useRef<HTMLDivElement>(null);
  const scRef = React.useRef<HTMLDivElement>(null);
  const cscRef = React.useRef<HTMLDivElement>(null);
  const idRef = React.useRef<HTMLDivElement>(null);
  const ecRef = React.useRef<HTMLDivElement>(null);

  const contactsVisible = useSelector(
    onboardingSelector.getSectionVisibility.contacts,
  );

  const sitesVisible = useSelector(
    onboardingSelector.getSectionVisibility.sites,
  );

  const TKOCompleted = useSelector(sessionSelector.hasCompletedTKO);

  const cloudSensorsVisible = useSelector(
    onboardingSelector.getSectionVisibility.cloudSensors,
  );

  const internetExposureVisible = useSelector(
    onboardingSelector.getSectionVisibility.internetExposure,
  );

  const endpointConfigVisible = useSelector(
    onboardingSelector.getSectionVisibility.endpoint,
  );

  const { sections, refMap } = React.useMemo(() => {
    const refMap: RefMap<OnboardingSections> = new Map();
    refMap.set(OnboardingSections.CHECKLIST, checklistRef);
    const sections: React.ReactNode[] = [];

    if (contactsVisible) {
      refMap.set(OnboardingSections.CONTACTS, contactsRef);
      sections.push(
        <div
          key={OnboardingSections.CONTACTS}
          ref={contactsRef}
          className="ob-item panel white-box"
        >
          <OnboardingContacts />
        </div>,
      );
    }

    if (sitesVisible) {
      refMap.set(OnboardingSections.SITE_CONFIG, scRef);
      sections.push(
        <div
          key={OnboardingSections.SITE_CONFIG}
          ref={scRef}
          className="ob-item panel white-box"
        >
          <OnboardingSiteConfig />
        </div>,
      );
    }

    if (endpointConfigVisible && !TKOCompleted) {
      refMap.set(OnboardingSections.ENDPOINT_CONFIG, ecRef);
      sections.push(
        <div
          key={OnboardingSections.ENDPOINT_CONFIG}
          ref={ecRef}
          className="ob-item panel white-box mb-5"
        >
          <OnboardingEndpointConfig />
        </div>,
      );
    }

    if (cloudSensorsVisible) {
      refMap.set(OnboardingSections.CLOUD_SENSOR_CONFIG, cscRef);
      sections.push(
        <div
          key={OnboardingSections.CLOUD_SENSOR_CONFIG}
          ref={cscRef}
          className="ob-item panel white-box"
        >
          <OnboardingCloudSensorConfig />
        </div>,
      );
    }

    if (internetExposureVisible) {
      refMap.set(OnboardingSections.INTERNET_EXPOSURE, idRef);
      sections.push(
        <div
          key={OnboardingSections.INTERNET_EXPOSURE}
          ref={idRef}
          className="ob-item panel white-box mb-5"
        >
          <OnboardingInternetExposure />
        </div>,
      );
    }

    return {
      refMap,
      sections,
    };
  }, [
    contactsVisible,
    sitesVisible,
    cloudSensorsVisible,
    internetExposureVisible,
    endpointConfigVisible,
    TKOCompleted,
  ]);

  const isOnboardingInProgress =
    contactsVisible ||
    sitesVisible ||
    cloudSensorsVisible ||
    internetExposureVisible;

  return (
    <Viewport>
      <PageTitle title="Onboarding" />
      <div className="d-flex">
        <div className="w-100">
          <div ref={checklistRef} className="ob-item panel white-box">
            <OnboardingChecklist />
          </div>
          {sections}
        </div>
        <OnboardingNavbar
          refMap={refMap}
          isOnboardingInProgress={isOnboardingInProgress}
        />
      </div>
    </Viewport>
  );
};

export default OnboardingView;
